import {
  Button,
  Flex,
  IconAddLine,
  IconArrowOpenDownLine,
  IconArrowOpenUpLine,
  IconButton,
  IconRefreshLine,
  IconTrashLine,
  Spinner,
  Text,
  TextInput,
  Tooltip,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useRef, useState } from "react";
import { AssessmentPlanService } from "../../../../services/AssessmentService";
import { ApDataContext } from "../../../../views/Plan";
import { Card } from "../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import CustomRTE, { EMPTY_RTE } from "../../../CustomTextArea/CustomRTE";
import { DialogYesNo } from "../../../DialogYesNo/DialogYesNo";
import AssessmentCard from "../ComponentCards/AssessmentCard";
import EmbeddedDashboardCard from "../ComponentCards/EmbeddedDashboardCard";
import MeasurePickerCard from "../ComponentCards/MeasurePickerCard";
import QualitativeMeasureCard from "../ComponentCards/QualitativeMeasureCard";
import SectionReviewersCard from "../Reviewers/SectionReviewersCard";
import { CANVAS, EMBEDDED, EXTERNAL, QUALITATIVE } from "../utils";

const PlanSectionCard = ({ lastSection, section }) => {
  const {
    sectionIndex,
    selectedCycle,
    handleCreateMeasure,
    handleDeleteMeasure,
    setSections,
    extraData,
  } = useContext(ApDataContext);
  const { t } = useTranslation();
  const showNotification = useNotification();
  const defaultDesc = useRef(section.description);
  const [runningIndex, setRunningIndex] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [enableUpdate, setEnableUpdate] = useState(false);
  const [enableCreate, setEnableCreate] = useState(false);
  const [upsertingSection, setUpsertingSection] = useState(false);

  const isApproversEqual = (approvers1, approvers2) => {
    if (approvers1.length !== approvers2.length) {
      return false;
    }
    for (let i = 0; i < approvers1.length; i++) {
      if (approvers1[i] !== approvers2[i]) {
        return false;
      }
    }
    return true;
  };

  const isReviewersEqual = (reviewers1, reviewers2) => {
    if (reviewers1.length !== reviewers2.length) {
      return false;
    }
    for (let i = 0; i < reviewers1.length; i++) {
      if (reviewers1[i] !== reviewers2[i]) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (section.id) {
      const currentSection = extraData.plan.sections.find(
        (s) => s.id === section.id
      );
      if (
        section.name !== currentSection.name ||
        section.description !== currentSection.description ||
        !isApproversEqual(section.approvers, currentSection.approvers) ||
        !isReviewersEqual(section.reviewers, currentSection.reviewers)
      ) {
        setEnableUpdate(true);
      } else {
        setEnableUpdate(false);
      }
    }
  }, [extraData, section]);

  useEffect(() => {
    if (
      section.measures.length > 0 &&
      !!section.name &&
      !!section.description &&
      section.description !== EMPTY_RTE
    ) {
      let missingMeasure = false;
      section.measures.forEach((measure) => {
        if (!measure.id && !measure.ddl_id) {
          missingMeasure = true;
        }
      });
      if (missingMeasure) {
        setEnableCreate(false);
      } else {
        setEnableCreate(true);
      }
    } else {
      setEnableCreate(false);
    }
  }, [section]);

  const moveSectionUp = () => {
    setSections((prevSections) =>
      prevSections.map((prevSection, i) =>
        i === sectionIndex - 1
          ? section
          : i === sectionIndex
            ? prevSections[sectionIndex - 1]
            : prevSection
      )
    );
  };

  const moveSectionDown = () => {
    setSections((prevSections) =>
      prevSections.map((prevSection, i) =>
        i === sectionIndex + 1
          ? section
          : i === sectionIndex
            ? prevSections[sectionIndex + 1]
            : prevSection
      )
    );
  };

  const updateSectionSequences = async (newSectionId) => {
    const sectionMeasureSequences = [];
    let offset = 0;
    extraData.plan.sections.forEach((section, i) => {
      if (i === sectionIndex) offset = 1;
      sectionMeasureSequences.push({ id: section.id, sequence: i + offset });
      section.measures.forEach((measure, j) => {
        sectionMeasureSequences.push({
          id: measure.id ?? measure.ddl_id,
          sequence: j,
        });
      });
    });
    sectionMeasureSequences.push({ id: newSectionId, sequence: sectionIndex });
    section.measures.forEach((measure, i) => {
      sectionMeasureSequences.push({
        id: measure.id ?? measure.ddl_id,
        sequence: i,
      });
    });

    await AssessmentPlanService.updateAssessmentPlanSequences(
      extraData.plan.id,
      sectionMeasureSequences
    );
  };

  const handleCreateSection = () => {
    setUpsertingSection(true);
    // Preparing payload to create section
    const newSection = JSON.parse(JSON.stringify(section));
    newSection.sequence = sectionIndex;
    newSection.assessmentList = [];
    newSection.qualitativeMeasures = [];
    newSection.measures.forEach((measure) => {
      if (measure.ddl_id) {
        newSection.qualitativeMeasures.push(measure.ddl_id);
      } else {
        newSection.assessmentList.push(measure.id);
      }
    });
    newSection.approvers = section.approvers;
    newSection.reviewers = section.reviewers.toString();
    delete newSection.measures;
    delete newSection.index;

    AssessmentPlanService.createAssessmentPlanSection(
      extraData.plan.id,
      newSection
    )
      .then(async (newSection) => {
        // Update section and measure sequences after new section is created
        await updateSectionSequences(newSection.id);
        // Update the sections list with the new section
        newSection.measures = section.measures;
        newSection.approvers = section.approvers;
        newSection.reviewers = newSection.reviewers.split(",");
        setSections((prevSections) => {
          const sections = prevSections.map((prevSection, i) =>
            i === sectionIndex ? newSection : prevSection
          );
          extraData.plan.sections = sections.filter((s) => !!s.id);
          return sections;
        });
        showNotification(
          t("notifications.general.createSuccess", { obj: "Section" })
        );
      })
      .catch(() =>
        showNotification(
          t("notifications.general.createError", { obj: "Section" }),
          ERROR
        )
      )
      .finally(() => setUpsertingSection(false));
  };

  const handleUpdateSection = () => {
    setUpsertingSection(true);
    AssessmentPlanService.updateAssessmentPlanSection(
      section.id,
      section.name,
      section.description,
      section.approvers,
      section.reviewers.toString()
    )
      .then((updatedSection) => {
        extraData.plan.sections = extraData.plan.sections.map((s) =>
          s.id === section.id
            ? {
                ...s,
                name: updatedSection.name,
                description: updatedSection.description,
                approvers: updatedSection.approvers,
                reviewers: updatedSection.reviewers.split(","),
              }
            : s
        );
        setEnableUpdate(false);
        showNotification(
          t("notifications.general.updateSuccess", { obj: "Section" })
        );
      })
      .catch(() =>
        showNotification(
          t("notifications.general.updateError", { obj: "Section" }),
          ERROR
        )
      )
      .finally(() => setUpsertingSection(false));
  };

  const handleDeleteSection = async () => {
    if (section.id) {
      await AssessmentPlanService.deleteAssessmentPlanSection(section.id)
        .then(() => {
          setSections((prevSections) => {
            const sections = prevSections.filter((_, i) => i !== sectionIndex);
            extraData.plan.sections = sections;
            return sections;
          });
        })
        .catch(() =>
          showNotification(
            t("notifications.general.deleteError", { obj: "Section" }),
            ERROR
          )
        );
    } else {
      setSections((prevSections) =>
        prevSections.filter((_, i) => i !== sectionIndex)
      );
    }
  };

  const handleHeadingChange = (e, index) => {
    setSections((prevSections) =>
      prevSections.map((prevSection, i) =>
        i === index ? { ...prevSection, name: e.target.value } : prevSection
      )
    );
  };

  const handleDescriptionChange = (value) => {
    setSections((prevSections) =>
      prevSections.map((prevSection, i) =>
        i === sectionIndex
          ? { ...prevSection, description: value }
          : prevSection
      )
    );
  };

  const handleAddMeasure = () => {
    setSections((prevSections) =>
      prevSections.map((prevSection, i) =>
        i === sectionIndex
          ? {
              ...prevSection,
              measures: [
                ...prevSection.measures,
                { index: runningIndex, type: CANVAS },
              ],
            }
          : prevSection
      )
    );
    setRunningIndex(runningIndex + 1);
  };

  return (
    <Card
      key={section.id ?? section.index}
      style={{
        backgroundColor: "#F1F1F1",
        border: "1px solid #ccc",
        marginTop: "0.75rem",
      }}
    >
      <Flex wrap="wrap" gap="small">
        <Flex.Item size="100%">
          <TextInput
            id={`section-heading-section-${section.id ?? section.index}`}
            renderLabel={
              <Flex gap="small" wrap="wrap">
                <Flex.Item shouldGrow>
                  {t("assessment.sections.sectionHeading")}
                </Flex.Item>
                <Flex.Item>
                  <Tooltip
                    renderTip={t("assessment.sections.moveSectionUp")}
                    on={["hover", "focus"]}
                  >
                    <IconButton
                      color="primary-inverse"
                      screenReaderLabel={t("assessment.sections.moveSectionUp")}
                      size="small"
                      renderIcon={IconArrowOpenUpLine}
                      onClick={moveSectionUp}
                      disabled={sectionIndex === 0}
                    />
                  </Tooltip>
                </Flex.Item>
                <Flex.Item>
                  <Tooltip
                    renderTip={t("assessment.sections.moveSectionDown")}
                    on={["hover", "focus"]}
                  >
                    <IconButton
                      color="primary-inverse"
                      screenReaderLabel={t(
                        "assessment.sections.moveSectionDown"
                      )}
                      size="small"
                      renderIcon={IconArrowOpenDownLine}
                      onClick={moveSectionDown}
                      disabled={!!lastSection}
                    />
                  </Tooltip>
                </Flex.Item>
                {!!extraData.plan && (
                  <Flex.Item>
                    {section.id ? (
                      <Button
                        color="primary"
                        screenReaderLabel={t(
                          "assessment.sections.updateSection"
                        )}
                        size="small"
                        renderIcon={
                          upsertingSection ? (
                            <Spinner
                              size="x-small"
                              renderTitle={t("global.pleaseWait")}
                            />
                          ) : (
                            IconRefreshLine
                          )
                        }
                        onClick={handleUpdateSection}
                        interaction={
                          enableUpdate && !upsertingSection
                            ? "enabled"
                            : "disabled"
                        }
                      >
                        {t("assessment.sections.updateSection")}
                      </Button>
                    ) : (
                      <Button
                        color="primary"
                        screenReaderLabel={t(
                          "assessment.sections.createSection"
                        )}
                        size="small"
                        renderIcon={
                          upsertingSection ? (
                            <Spinner
                              size="x-small"
                              renderTitle={t("global.pleaseWait")}
                            />
                          ) : (
                            IconAddLine
                          )
                        }
                        onClick={handleCreateSection}
                        interaction={
                          enableCreate && !upsertingSection
                            ? "enabled"
                            : "disabled"
                        }
                      >
                        {t("assessment.sections.createSection")}
                      </Button>
                    )}
                  </Flex.Item>
                )}
                <Flex.Item>
                  <Tooltip
                    renderTip={t("assessment.sections.deleteSection")}
                    on={["hover", "focus"]}
                  >
                    <IconButton
                      color="danger"
                      screenReaderLabel={t("assessment.sections.deleteSection")}
                      size="small"
                      renderIcon={IconTrashLine}
                      onClick={() => setDeleteConfirm(true)}
                    />
                  </Tooltip>
                </Flex.Item>
              </Flex>
            }
            defaultValue={section.name}
            onBlur={(e) => handleHeadingChange(e, sectionIndex)}
          />
        </Flex.Item>
        <Flex.Item size="100%">
          <CustomRTE
            id="section-desc"
            label={
              <Text weight="bold">{t("assessment.sections.sectionDesc")}</Text>
            }
            defaultValue={defaultDesc.current}
            onChange={handleDescriptionChange}
          />
        </Flex.Item>
        <Flex.Item size="100%">
          <SectionReviewersCard section={section} />
        </Flex.Item>
        <Flex.Item size="100%">
          <Text weight="bold">{t("measures.measures")}:</Text>
          <Flex wrap="wrap" gap="small" margin="small 0 0 0">
            {section.measures.map((measure, key) => (
              <Flex.Item
                key={`section${sectionIndex}-measure${key}`}
                size="100%"
              >
                <ApDataContext.Provider
                  value={{
                    sectionIndex,
                    measureIndex: key,
                    selectedCycle,
                    handleCreateMeasure,
                    handleDeleteMeasure,
                    setSections,
                    extraData,
                    sectionId: section.id,
                  }}
                >
                  {measure.id || measure.ddl_id ? (
                    <>
                      {(measure.type === CANVAS ||
                        measure.type === EXTERNAL) && (
                        <AssessmentCard
                          assessment={measure}
                          lastMeasure={key === section.measures.length - 1}
                        />
                      )}
                      {measure.type === QUALITATIVE && (
                        <QualitativeMeasureCard
                          measure={measure}
                          lastMeasure={key === section.measures.length - 1}
                        />
                      )}
                      {measure.type === EMBEDDED && (
                        <EmbeddedDashboardCard
                          measure={measure}
                          lastMeasure={key === section.measures.length - 1}
                        />
                      )}
                    </>
                  ) : (
                    <MeasurePickerCard
                      measure={measure}
                      lastMeasure={key === section.measures.length - 1}
                    />
                  )}
                </ApDataContext.Provider>
              </Flex.Item>
            ))}
            <Flex.Item textAlign="end" shouldGrow>
              <Button
                color="primary"
                renderIcon={IconAddLine}
                onClick={handleAddMeasure}
              >
                {t("measures.addMeasure")}
              </Button>
            </Flex.Item>
          </Flex>
        </Flex.Item>
      </Flex>
      <DialogYesNo
        message={t("assessment.sections.deleteSectionConfirm")}
        openStatus={deleteConfirm}
        onClickNo={() => setDeleteConfirm(false)}
        onClickYes={handleDeleteSection}
      />
    </Card>
  );
};

export default PlanSectionCard;
